import styled from "@emotion/styled";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { useIsMobile } from "../../../hooks/useDeviceType";
import { useWindowSize } from "usehooks-ts";
import { useMemo } from "react";
import { getCardWidth } from "./element/Carousel/Card";
import { Carousel, CAROUSEL_FLEX_CONTAINER_GAP } from "./element/Carousel/Carousel";
import { HomepageCard } from "./element/Carousel/HomepageCard";
import { Theme } from "../../../styles/theme";
import owlImgSrc from "../../../img/home/owl.png";
import whaleImgSrc from "../../../img/home/whale.png";
import waterBlobImgSrc from "../../../img/home/water-blob.png";
import squarePatternImgSrc from "../../../img/home/square-pattern.png";

export const FeatureCards = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const isMobile = useIsMobile();
  const { width: windowWidth } = useWindowSize();
  const cardContents = getCardContents(theme, isMobile);
  const shouldUseCarousel = useMemo(() => {
    const cardCount = cardContents.length;
    const cardWidth = getCardWidth(isMobile);
    return cardWidth * cardCount + CAROUSEL_FLEX_CONTAINER_GAP * cardCount >= windowWidth;
  }, [cardContents.length, isMobile, windowWidth]);
  const Wrapper = shouldUseCarousel ? Carousel : NonCarouselContainer;

  return (
    <Wrapper style={{ marginTop: 48, color: theme.button.secondary.text.default, zIndex: 1 }}>
      {cardContents.map(entry => (
        <HomepageCard key={entry.title} title={entry.title} body={entry.body} color={entry.color}>
          {entry.children}
        </HomepageCard>
      ))}
    </Wrapper>
  );
};

// Helpers
export const NonCarouselContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;
const getCardContents = (theme: Theme, isMobile: boolean) => [
  {
    title: "Best prices for you. Solved",
    body: "Get the best possible execution for every trade as independent algorithms compete in our auctions to deliver for you.",
    color: theme["Brand colours"].Red,
    children: (
      <img
        src={owlImgSrc}
        alt="Intelligent owl"
        width={isMobile ? 207 : 272}
        style={{ position: "absolute", bottom: 0, right: 0 }}
      />
    ),
  },
  {
    title: "Deep liquidity",
    body: "We reach into the widest net of exchanges, pools and professional market makers, so that you can trade any token, at any size.",
    color: theme["Brand colours"].Sea,
    children: (
      <>
        <div style={{ marginTop: 8 }}></div>
        <img
          src={whaleImgSrc}
          alt="Whale on a deep dive"
          width={isMobile ? 210 : 312}
          style={{ position: "absolute", bottom: 0, right: 0 }}
        />
      </>
    ),
  },
  {
    title: "Gas efficiency",
    body: "Network fees can get unpleasantly expensive. Our optimized design and multi-token execution reduce these costs by up to 80%.",
    color: theme["Brand colours"]["Baby Blue"],
    children: (
      <img
        src={waterBlobImgSrc}
        alt="Sparkling water droplet"
        width={isMobile ? 160 : 240}
        style={{ position: "absolute", bottom: 0, right: 30 }}
      />
    ),
  },
  {
    title: "Built for success",
    body: "You can rely on us to ensure that your transactions succeed with the highest level of confidence. And in the unlikely event of failure, you won't have to pay.",
    color: theme["Brand colours"].Green,
    children: (
      <img
        src={squarePatternImgSrc}
        alt="Square pattern"
        width={isMobile ? 180 : 232}
        style={{ position: "absolute", bottom: 0, right: 0 }}
      />
    ),
  },
];
